import { Tooltip } from 'antd';
import { useSession } from 'next-auth/react';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import DragDropSvg from '../../../assets/icons/DragDropSvg';
import FlashSvg from '../../../assets/icons/FlashSvg';
import ShareSvg from '../../../assets/icons/ShareSvg';
import theme from '../../../assets/theme';
import useAutoFade from '../../../hooks/useAutoFade';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import { useMetrics } from '../../../hooks/useMetrics';
import usePreviewGame from '../../../hooks/usePreviewGame';
import useRouterMatch from '../../../hooks/useRouterMatch';
import { useMarketingMetrics } from '../../../providers/PlausibleAnalyticsProvider';
import { Game } from '../../../services/backendService/types';
import { getAggregatedSlidesAndActivities } from '../../../utils/roundUtils';
import { IconWrapper } from '../../_atoms/IconWrapper';
import ShareResultsModal from '../../_molecules/ShareResultsModal';
import { Slide } from '../../SlideComponents/slide.types';
import { findRelatedSlidesWithoutIndex } from '../../SlideComponents/slideUtil';
import {
  LiveGameActionButton,
  LiveGameActionButtonContainer,
  LiveGameNavigationActions,
} from '../LiveGameActionButtons/LiveGameActionButtons';

const LiveGameActionViewerButtons = ({
  slides,
  showActivityMedia,
  localGame,
  currentSlide,
  setShowMedia,
  setLocalGame,
}: {
  slides: Slide[];
  currentSlide: string;
  localGame: Game;
  setLocalGame: Dispatch<SetStateAction<Game | undefined>>;
  setShowMedia: Dispatch<SetStateAction<boolean>>;
  showActivityMedia: boolean;
}) => {
  const metrics = useMetrics();
  const { router, tenantId } = useRouterMatch();
  const numberOfSlides = useMemo(() => {
    return getAggregatedSlidesAndActivities(slides || []).length;
  }, [slides]);

  const session = useSession();

  const currentSlideRound = useMemo(() => {
    return currentSlide
      ? findRelatedSlidesWithoutIndex(currentSlide, slides)[0]
      : undefined;
  }, [currentSlide, slides]);
  const currentSlideRoundIndex = useMemo(() => {
    const slideRounds = getAggregatedSlidesAndActivities(slides || []);
    return currentSlideRound ? slideRounds.indexOf(currentSlideRound) : 0;
  }, [currentSlideRound, slides]);
  const canShowMedia = useMemo(() => {
    if (currentSlideRound?.type === 'slide') return false;
    return true;
  }, [currentSlideRound]);

  const { isInvisible } = useAutoFade({
    delay: 1000 * 2,
    eventListenerDependency: router.pathname,
  });

  const handleSkipBack = () => {
    if (currentSlideRoundIndex > 0) {
      if (session.status === 'authenticated') {
        metrics.logEvent('LiveGameViewer.GoBack.Clicked');
      }
      const currentRelatedRounds = findRelatedSlidesWithoutIndex(currentSlide, slides);
      const firstRelatedRound = currentRelatedRounds[0];
      const firstRelatedRoundIndex = slides.findIndex(
        slide => slide.id === firstRelatedRound.id,
      );
      const previousRound = slides[firstRelatedRoundIndex - 1];
      const related = findRelatedSlidesWithoutIndex(previousRound.id, slides);
      const wasPlayed = related.some(r => r.wasPlayed);
      const newRound = related[wasPlayed ? related.length - 1 : 0];
      const updatedLocalGame = {
        ...localGame,
        currentRound: newRound.id,
      };
      setLocalGame(updatedLocalGame);
      setShowMedia(false);
    }
  };
  const handleSkipForward = () => {
    if (currentSlideRoundIndex + 1 < numberOfSlides) {
      if (session.status === 'authenticated') {
        metrics.logEvent('LiveGameViewer.GoForward.Clicked');
      }
      const currentRelatedRounds = findRelatedSlidesWithoutIndex(currentSlide, slides);
      const lastRelatedRound = currentRelatedRounds[currentRelatedRounds.length - 1];
      const lastRelatedRoundIndex = slides.findIndex(
        round => round.id === lastRelatedRound.id,
      );
      const nextRound = slides[lastRelatedRoundIndex + 1];
      const related = findRelatedSlidesWithoutIndex(nextRound.id, slides);
      const wasPlayed = related.some(r => r.wasPlayed);

      setLocalGame({
        ...localGame,
        currentRound: related[wasPlayed ? related.length - 1 : 0].id,
      });
      setShowMedia(false);
    }
  };
  const { isPreview } = usePreviewGame();
  return (
    <LiveGameActionButtonContainer $isInvisible={isInvisible}>
      {currentSlide && (
        <ShareAction
          currentRound={currentSlide}
          gameId={localGame.id}
          tenantId={tenantId}
        />
      )}
      {canShowMedia && !isPreview && <LiveGameWidgetDivider />}
      {canShowMedia && !isPreview && (
        <ShowMediaAction
          setShowMedia={setShowMedia}
          showActivityMedia={showActivityMedia}
        />
      )}
      <LiveGameWidgetDivider />
      <LiveGameNavigationActions
        isNavigationDisabled={false}
        currentSlideType={currentSlideRound?.type}
        numberOfSlides={numberOfSlides}
        handleSkipBack={handleSkipBack}
        handleSkipForward={handleSkipForward}
        currentSlideRoundIndex={currentSlideRoundIndex}
      />
    </LiveGameActionButtonContainer>
  );
};

const ShowMediaAction = ({
  setShowMedia,
  showActivityMedia,
}: {
  setShowMedia: Dispatch<SetStateAction<boolean>>;
  showActivityMedia: boolean;
}) => {
  const { t } = useTranslation('Host');
  const isLargerThan900 = useBreakPoint(900);
  const metrics = useMetrics();
  const session = useSession();
  return (
    <ShowMediaContainer>
      <Tooltip
        title={
          showActivityMedia
            ? t('Click to show the activity to start')
            : t('Click to show image or video related to activity')
        }
        mouseEnterDelay={2}
      >
        <LiveGameActionButton
          style={{ display: 'flex' }}
          onClick={() => {
            if (session.status === 'authenticated') {
              metrics.logEvent('LiveGameViewer.ShowMedia.Clicked');
            }
            setShowMedia(e => !e);
          }}
        >
          <IconWrapper height={'25px'} width={'25px'}>
            {showActivityMedia ? <FlashSvg /> : <DragDropSvg />}
          </IconWrapper>
          {isLargerThan900 && (
            <span style={{ marginLeft: '5px' }}>
              {showActivityMedia ? t('Show results') : t('Show slide')}
            </span>
          )}
        </LiveGameActionButton>
      </Tooltip>
    </ShowMediaContainer>
  );
};

const ShareAction = ({
  gameId,
  tenantId,
  currentRound,
}: {
  gameId: string;
  tenantId: string;
  currentRound: string;
}) => {
  const { t } = useTranslation('Host');
  const metrics = useMetrics();
  const session = useSession();
  const { track } = useMarketingMetrics();
  const [showShareModal, setShowShareModal] = useState(false);
  return (
    <ShareActionsContainer>
      {showShareModal && (
        <ShareResultsModal
          gameId={gameId}
          tenantId={tenantId}
          setShowShareResults={setShowShareModal}
          showShareResults={showShareModal}
          gameTitle={t('Share results')}
          currentRoundId={currentRound}
        />
      )}
      <Tooltip title={t('Open link to this lesson to share')} mouseEnterDelay={2}>
        <LiveGameActionButton
          onClick={() => {
            if (session.status === 'authenticated') {
              metrics.logEvent('LiveGameViewer.Share.Open');
            } else {
              track('LiveGameViewer.Share.Open');
            }
            setShowShareModal(true);
          }}
        >
          <IconWrapper height={'25px'} width={'25px'} style={{ marginRight: '10px' }}>
            <ShareSvg />
          </IconWrapper>

          {t('Share')}
        </LiveGameActionButton>
      </Tooltip>
    </ShareActionsContainer>
  );
};

const ShowMediaContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 5px;
  height: 100%;
`;
const ShareActionsContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 5px;
  height: 100%;
`;
const LiveGameWidgetDivider = styled.div`
  margin: 0;
  border-right: solid 1px ${theme.colors.grey};
  height: 100%;
`;

export default LiveGameActionViewerButtons;
